/**
 * Admin Analytics dashboard (Sprint 91).
 * Renders MTD revenue, top routes (last 30d), conversion funnel, user growth.
 */
import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Package, Users, Mail, Send, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";

const Card = ({ label, value, sub, accent }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-[#FF6B00] bg-orange-50" : "border-slate-200 bg-white"}`}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent ? "text-[#FF6B00]" : "text-slate-900"}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics/overview");
        if (!cancelled) { setData(data); setLoading(false); }
      } catch (e) {
        if (!cancelled) setLoading(false);
        toast.error("Failed to load analytics");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fireTestEmail = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/test-email", { to: testEmail || undefined });
      if (r.ok) toast.success(`Test email sent to ${r.sent_to}`);
      else toast.error(r.error || "Send failed — check backend logs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <RoleGate roles={["admin", "founder", "ceo"]}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Activity className="h-7 w-7 text-[#FF6B00]" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
            <p className="text-xs text-slate-500">Live numbers from the order ledger</p>
          </div>
        </div>

        {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}

        {data && (
          <>
            <h2 className="text-sm font-semibold text-slate-700 pt-2">Month to Date</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card accent label="Revenue MTD" value={`₹${(data.mtd.revenue || 0).toLocaleString("en-IN")}`} sub={`${data.mtd.order_count} orders`} />
              <Card label="Avg ticket" value={`₹${(data.mtd.avg_ticket || 0).toLocaleString("en-IN")}`} />
              <Card label="Orders MTD" value={data.mtd.order_count} />
            </div>

            <h2 className="text-sm font-semibold text-slate-700 pt-3">Last 30 days · Conversion funnel</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Card label="Orders" value={data.last_30d.orders} />
              <Card label="Payment intents" value={data.last_30d.payment_intents} />
              <Card label="Consumed" value={data.last_30d.consumed} sub="Successfully paid" />
              <Card accent label="Conversion %" value={data.last_30d.conversion_pct != null ? `${data.last_30d.conversion_pct}%` : "—"} sub={`${data.last_30d.abandoned} abandoned`} />
            </div>

            <h2 className="text-sm font-semibold text-slate-700 pt-3">Top routes (last 30 days)</h2>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {(data.top_routes_30d || []).length === 0 ? (
                <p className="px-4 py-3 text-xs text-slate-500">No routes yet</p>
              ) : data.top_routes_30d.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-slate-900">{r.route}</span>
                  <span className="text-xs font-semibold text-slate-500">{r.count} orders</span>
                </div>
              ))}
            </div>

            <h2 className="text-sm font-semibold text-slate-700 pt-3">Users</h2>
            <div className="grid grid-cols-2 gap-3">
              <Card label="Total users" value={data.users.total} />
              <Card label="New (30d)" value={data.users.new_30d} />
            </div>
          </>
        )}

        {/* Test email block */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-4 w-4 text-[#FF6B00]" />
            <h2 className="text-sm font-semibold text-slate-900">SMTP test</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">Fire a quick test email to confirm Gmail SMTP is wired correctly. Used after rotating app-passwords.</p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="leave blank to send to your own email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
              data-testid="test-email-input"
            />
            <button
              onClick={fireTestEmail}
              disabled={sending}
              className="px-4 py-2 rounded-lg bg-[#FF6B00] text-white text-sm font-medium hover:bg-[#E55A00] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="test-email-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send test
            </button>
          </div>
        </div>
      </div>
    </RoleGate>
  );
}
